/**
 * "N products impacted" headline banner for the cascade view (P2.6.e).
 *
 * Reads counts directly from the CascadeSummary response -- no client-
 * side aggregation. The banner number always matches what the API
 * said, so a refresh tick can't briefly disagree with the per-impact
 * card list below.
 */
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { CascadeSummary } from "shared/api/types";
import { formatSAR, formatSARCompact } from "shared/money/money";

import { Arrow } from "shared/components/Arrow";
export interface CascadeBannerProps {
  summary: CascadeSummary;
}

export function CascadeBanner({ summary }: CascadeBannerProps) {
  const { t } = useTranslation();

  const criticalCount = summary.impacted_by_severity.critical ?? 0;
  const highCount = summary.impacted_by_severity.high ?? 0;

  /* Round 17 B11 -- the banner was mint over "0 products impacted
     across 0 entities", directly above "Not yet assessed ... the
     absence of a verdict, not a clean one". Green is a verdict; over an
     estate nothing has judged the banner is neutral. */
  const tone =
    summary.product_count > 0
      ? "bg-red-50 border-red-200 text-red-900"
      : summary.estate_assessed === false
        ? "bg-slate-50 border-slate-200 text-slate-800"
        : "bg-emerald-50 border-emerald-200 text-emerald-900";

  // P14.1 (#478): SAR revenue rollup. Surface only when at least one
  // measured product made it into the impacted set -- showing
  // "SAR 0 daily at risk" when no products are impacted would
  // confuse the operator (zero is a measurement, not a non-result).
  const measuredCount = summary.revenue_measured_count;
  const totalProducts = summary.product_count;
  const showRevenue = measuredCount > 0;
  // Round 17 H34 -- a headline figure at a glance: "SAR 7.32B", not
  // "SAR 7,321,400,000". The exact figure stays in the title.
  const annualLabel = formatSARCompact(summary.total_annual_revenue_at_risk);
  const dailyLabel = formatSARCompact(summary.total_daily_revenue_at_risk);

  // P23.2 (#524): inspect impacted products to figure out which
  // rollup mechanism produced the SAR figure.
  //   "split"   -> at least one product carries explicit shares
  //   "uniform" -> every measured product used uniform-split
  //   null      -> no measured products; hint hidden
  const measuredImpactedProducts = summary.impacted_products.filter(
    (p) => p.annual_revenue_at_risk !== null,
  );
  const hasSplit = measuredImpactedProducts.some((p) => p.at_risk_mode === "split");
  const splitHint: "split" | "uniform" | null =
    measuredImpactedProducts.length === 0 ? null : hasSplit ? "split" : "uniform";

  // P20.1 (#519): MVB intersection chip. Hidden when the bank has
  // declared zero MVB IBSes (mvb_impacted_share is null) -- showing
  // "0 of 0 MVB IBSes impacted" would confuse the operator before
  // any MVB has been declared. Same posture as the revenue chip.
  const showMvb = summary.mvb_total_count > 0;
  const mvbPct = Math.round((summary.mvb_impacted_share ?? 0) * 100);

  // P258: estate-wide IBS resilience RAG from the sealed resilience-state
  // chain (dependency-propagated). Distinct from the finding-walk counts
  // -- a CI finding can drive an IBS amber with cascade_ibs_count still 0.
  // Hidden when every IBS is green/unscored. The chips link to the
  // constellation operations wall (P260 follow-on) where each red/amber
  // node's sealed resilience provenance is one click away.
  const ibsRed = summary.ibs_resilience_red ?? 0;
  const ibsAmber = summary.ibs_resilience_amber ?? 0;
  const showResilience = ibsRed > 0 || ibsAmber > 0;

  return (
    <section
      data-testid="cascade-banner"
      className={`rounded-lg border ${tone} px-5 py-4 mb-5`}
    >
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h2 className="text-xl font-semibold">
          {t("modules.twin.cascade.banner.productsImpacted", {
            count: summary.product_count,
          })}
        </h2>
        <span className="text-sm opacity-80">
          {t("modules.twin.cascade.banner.entitiesImpacted", {
            count: summary.impacted_count,
          })}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs opacity-80">
        {criticalCount > 0 && (
          <span data-testid="cascade-banner-critical">
            {t("modules.twin.cascade.banner.severityCritical", {
              count: criticalCount,
            })}
          </span>
        )}
        {highCount > 0 && (
          <span data-testid="cascade-banner-high">
            {t("modules.twin.cascade.banner.severityHigh", { count: highCount })}
          </span>
        )}
        <span data-testid="cascade-banner-stats" className="tabular-nums">
          {t("modules.twin.cascade.banner.cascadeStats", {
            nodes: summary.cascade_node_count,
            ibses: summary.cascade_ibs_count,
          })}
        </span>
      </div>
      {showRevenue && (
        <div
          data-testid="cascade-banner-revenue"
          className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium tabular-nums"
        >
          <span
            data-testid="cascade-banner-revenue-daily"
            title={formatSAR(summary.total_daily_revenue_at_risk)}
          >
            {t("modules.twin.cascade.banner.revenueAtRiskDaily", {
              amount: dailyLabel,
            })}
          </span>
          <span
            data-testid="cascade-banner-revenue-annual"
            title={formatSAR(summary.total_annual_revenue_at_risk)}
          >
            {t("modules.twin.cascade.banner.revenueAtRiskAnnual", {
              amount: annualLabel,
            })}
          </span>
          {splitHint && (
            <span
              data-testid="cascade-banner-split-hint"
              data-mode={splitHint}
              className="opacity-75"
            >
              {splitHint === "split"
                ? t("modules.twin.cascade.banner.splitHintExplicit")
                : t("modules.twin.cascade.banner.splitHintUniform")}
            </span>
          )}
          <span data-testid="cascade-banner-revenue-measured" className="opacity-75">
            {measuredCount === totalProducts
              ? t("modules.twin.cascade.banner.revenueMeasuredAll", {
                  total: totalProducts,
                })
              : t("modules.twin.cascade.banner.revenueMeasured", {
                  measured: measuredCount,
                  total: totalProducts,
                })}
          </span>
        </div>
      )}
      {showMvb && (
        <div className="mt-2 flex flex-wrap gap-x-2 gap-y-1">
          <span
            data-testid="cascade-banner-mvb"
            data-mvb-impacted={summary.mvb_impacted_count}
            data-mvb-total={summary.mvb_total_count}
            data-mvb-pct={mvbPct}
            className="rounded bg-indigo-50 ring-1 ring-indigo-200 text-indigo-800 px-2 py-0.5 text-xs font-medium tabular-nums"
          >
            {t("modules.twin.cascade.banner.mvbIntersection", {
              impacted: summary.mvb_impacted_count,
              total: summary.mvb_total_count,
              pct: mvbPct,
            })}
          </span>
        </div>
      )}
      {showResilience && (
        <div
          data-testid="cascade-banner-resilience"
          className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1"
        >
          {ibsRed > 0 && (
            <Link
              to="/twin/constellation"
              data-testid="cascade-banner-resilience-red"
              data-count={ibsRed}
              title={t("modules.twin.cascade.banner.resilienceLink")}
              className="rounded bg-rose-50 ring-1 ring-rose-200 text-rose-800 px-2 py-0.5 text-xs font-medium tabular-nums hover:ring-rose-400"
            >
              {t("modules.twin.cascade.banner.resilienceRed", { count: ibsRed })}
            </Link>
          )}
          {ibsAmber > 0 && (
            <Link
              to="/twin/constellation"
              data-testid="cascade-banner-resilience-amber"
              data-count={ibsAmber}
              title={t("modules.twin.cascade.banner.resilienceLink")}
              className="rounded bg-amber-50 ring-1 ring-amber-200 text-amber-800 px-2 py-0.5 text-xs font-medium tabular-nums hover:ring-amber-400"
            >
              {t("modules.twin.cascade.banner.resilienceAmber", {
                count: ibsAmber,
              })}
            </Link>
          )}
          <span className="text-[11px] text-slate-400">
            {t("modules.twin.cascade.banner.resilienceLink")} <Arrow />
          </span>
        </div>
      )}
    </section>
  );
}
